<template>
  <b-card class="!border-[1px] !border-[#E2E2E2] !border-solid !shadow-none !-mt-[10px]">
    <h3 class="font-semibold text-[#333] text-[20px] pb-[16px] mb-0">
      Buat Campaign
    </h3>
    <Step :current-step="currentStep" />

    <validation-observer
      ref="formRules"
      #default="{ errors }"
    >
      <b-form
        ref="formRef"
        @submit.prevent
      >
        <div
          v-show="currentStep === 0"
          class="p-[14px] bg-[#FCFCFC] my-[16px] rounded-[10px] flex flex-col gap-[24px]"
        >
          <collapsible
            v-for="item in fields"
            :key="item.key"
            :title="item.label"
            :icon="item.icon"
            :visible="sectionStates[item.key]"
            :has-error="getSectionErrorCount(item.key, errors) > 0"
            :error-count="getSectionErrorCount(item.key, errors)"
            @update:visible="val => $set(sectionStates, item.key, val)"
          >
            <template v-for="input in item.inputs">
              <!-- Group type inputs -->
              <div
                v-if="input.type === 'group'"
                :key="input.key || (input.children && input.children[0] && input.children[0].key)"
                class="flex gap-[16px]"
              >
                <validated-input
                  v-for="child in input.children"
                  :key="child.key"
                  v-model="form[child.key]"
                  :item="child"
                  class="flex-1"
                />
              </div>

              <validated-input
                v-else
                :key="input.key"
                v-model="form[input.key]"
                :item="input"
              />
            </template>
          </collapsible>
        </div>

        <div
          v-show="currentStep !== 0"
          class="py-[24px]"
        >
          <template v-if="hasVisitedStep1">
            <div class="flex flex-wrap mb-[30px] gap-[10px] items-center text-[#08A0F7] border-[1px] border-[#08A0F7] py-[10px] px-[14px] rounded-[8px] bg-[#DFF3FF]">
              <img
                src="https://storage.googleapis.com/komerce/assets/icons/star-1-filled-blue.svg"
                alt="preview-campaign"
                width="24px"
                height="24px"
              >
              <span>Berikut preview campaign kamu dan rekomendasi creator yang paling relevan berdasarkan kriteria yang diisi.</span>
            </div>
            <preview
              :form="form"
            />
            <creator-recomendation
              :form="form"
            />
          </template>
        </div>

        <div class="flex justify-end flex-wrap items-center gap-[12px] px-[24px] -mx-[21px] py-[16px] border-t-[1px] border-[#E2E2E2]">
          <b-button
            v-if="currentStep > 0"
            variant="outline-primary"
            class="min-w-[120px] h-[40px] rounded-[6px] !border-[#F95031] !text-[#F95031] hover:bg-[#FFEBE7] font-semibold"
            @click="prevStep"
          >
            Kembali
          </b-button>
          <b-button
            v-else
            variant="outline-secondary"
            class="min-w-[120px] h-[40px] rounded-[6px] !border-[#E2E2E2] !text-[#626262] hover:bg-[#F4F4F4] font-semibold"
            :disabled="loadingSubmit"
            @click="$router.back()"
          >
            Batal
          </b-button>

          <b-button
            v-if="currentStep === 0"
            variant="primary"
            class="min-w-[120px] h-[40px] rounded-[6px] !bg-[#F95031] !border-[#F95031] hover:!bg-[#E04427] font-semibold text-white"
            @click="nextStep"
          >
            Selanjutnya
          </b-button>
          <template
            v-if="currentStep === 1"
          >
            <b-button
              variant="primary"
              class="min-w-[120px] h-[40px] rounded-[6px] !bg-[#F95031] !border-[#F95031] hover:!bg-[#E04427] font-semibold text-white gap-[8px] !flex items-center justify-center"
              :disabled="loadingSubmit || loadingAsDraft"
              @click="saveAsDraft"
            >
              Draft
              <b-spinner
                v-if="loadingAsDraft"
                small
              />
            </b-button>
            <b-button
              variant="primary"
              class="min-w-[120px] h-[40px] rounded-[6px] !bg-[#F95031] !border-[#F95031] hover:!bg-[#E04427] font-semibold text-white gap-[8px] !flex items-center justify-center"
              :disabled="loadingSubmit || loadingAsDraft"
              @click="submitCampaign"
            >
              Buat Campaign
              <b-spinner
                v-if="loadingSubmit"
                small
              />
            </b-button>
          </template>
        </div>
      </b-form>
    </validation-observer>
  </b-card>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { alertSuccess } from '@toast'
import { YEAR_MONTH_DAY } from '@/libs/filterDate'
import Step from './Step.vue'
import { CREATE_CAMPAIGN_FORM_FIELDS, CAMPAIGN_FORM_TAP, CAMPAIGN_FORM_MCN } from '../config'
import Collapsible from './Collapsible.vue'
import ValidatedInput from './ValidatedInput.vue'
import Preview from './Preview.vue'
import CreatorRecomendation from './CreatorRecomendation.vue'
import { getSectionErrorCount, handleValidationErrors } from '../composables/useCampaignValidation'

export default {
  components: {
    Step,
    Collapsible,
    ValidatedInput,
    ValidationObserver,
    Preview,
    CreatorRecomendation,
  },
  data() {
    return {
      currentStep: 0,
      hasVisitedStep1: false,
      form: {
        product_type: 'Free',
      },
      sectionStates: {
        campaign_info: true,
        scope_of_works: false,
        agreements: false,
      },
      alertSuccess,
      CREATE_CAMPAIGN_FORM_FIELDS,
      loadingSubmit: false,
      loadingAsDraft: false,
    }
  },
  computed: {
    aggreementField() {
      const channel = String(this.form.channel || '').toLowerCase()
      const isMcnOnly = channel.includes('mcn') && !channel.includes('tap')
      return {
        label: 'Agreements',
        key: 'agreements',
        icon: 'k-document-text',
        inputs: isMcnOnly ? CAMPAIGN_FORM_MCN : CAMPAIGN_FORM_TAP,
      }
    },
    fields() {
      const baseFields = CREATE_CAMPAIGN_FORM_FIELDS.map(section => ({
        ...section,
        inputs: section.inputs.map(input => {
          if (input.type !== 'group') return input
          return {
            ...input,
            children: input.children.map(child => {
              if (child.key !== 'end_date') return child
              return {
                ...child,
                disabledDates: this.form.start_date
                  ? { to: new Date(this.form.start_date) }
                  : undefined,
              }
            }),
          }
        }),
      }))
      return [...baseFields, this.aggreementField]
    },
  },
  watch: {
    currentStep(val) {
      if (val >= 1) this.hasVisitedStep1 = true
    },
  },
  methods: {
    getFormData({
      status,
    }) {
      const formData = new FormData()
      Object.entries(this.form).forEach(([key, value]) => {
        if (key === 'thumbnail') {
          const file = Array.isArray(value) ? value[0] : value
          formData.append('thumbnail', file)
          return
        }
        if (key === 'start_date' || key === 'end_date') {
          const datetime = YEAR_MONTH_DAY(value)
          formData.append(key, datetime)
          return
        }
        if (key === 'campaign_type' && value.includes('Add-on')) {
          formData.append('addon_reimburse', true)
        }
        if (Array.isArray(value)) {
          value.forEach(item => formData.append(key, item))
        } else if (value !== null && value !== undefined) {
          formData.append(key, value)
        }
      })
      formData.append('status', status)
      return formData
    },
    getSectionErrorCount(sectionKey, errors) {
      return getSectionErrorCount(sectionKey, errors)
    },
    handleValidationErrors() {
      handleValidationErrors({
        sectionStates: this.sectionStates,
        errors: this.$refs.formRules?.errors,
      })
    },
    async nextStep() {
      if (this.currentStep > 0) return
      const isValid = await this.$refs.formRules.validate()
      if (!isValid) {
        this.handleValidationErrors()
        return
      }
      this.currentStep = 1
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep = 0
      }
    },
    async saveAsDraft() {
      this.loadingAsDraft = true
      try {
        const isValid = await this.$refs.formRules.validate()
        if (!isValid) {
          this.loadingAsDraft = false
          this.currentStep = 0
          this.handleValidationErrors()
          return
        }

        const formData = this.getFormData({
          status: 'draft',
        })

        await this.$http_new.post('/komclass/api/v1/komlabs/campaigns/create', formData)

        this.$toast_success({
          title: 'Campaign berhasil disimpan ke draft.',
          message: 'Campaign Anda sudah tersimpan ke dalam draft',
        })

        this.$router.push({ name: 'campaign-list-komlabs' })
      } catch (err) {
        this.$toast_error({
          title: 'Gagal menyimpan campaign ke draft',
          message: 'Terjadi kesalahan. Silakan coba lagi.',
        })
      } finally {
        this.loadingAsDraft = false
      }
    },
    async submitCampaign() {
      this.loadingSubmit = true
      try {
        const isValid = await this.$refs.formRules.validate()
        if (!isValid) {
          this.loadingSubmit = false
          this.currentStep = 0
          this.handleValidationErrors()
          return
        }

        const formData = this.getFormData({
          status: 'pre_campaign',
        })

        await this.$http_new.post('/komclass/api/v1/komlabs/campaigns/create', formData)

        this.$toast_success({
          title: 'Campaign berhasil dibuat ',
          message: 'Campaign Anda sudah aktif dan masuk ke daftar Pre-Campaign.',
        })

        this.$router.push({ name: 'campaign-list-komlabs' })
      } catch (err) {
        this.$toast_error({
          title: 'Gagal membuat campaign',
          message: 'Terjadi kesalahan. Silakan coba lagi.',
        })
      } finally {
        this.loadingSubmit = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
